import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import { useTranslation } from 'next-i18next'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import React, { useEffect } from 'react'
import { numberWithCommas } from 'src/utils'
import {
  getOnDemandHeroImage,
  isSafeReturnPath,
  OnDemandCityConfig
} from 'src/utils/onDemand'
import {
  onDemandView,
  onDemandWhatsapp,
  viewOnDemandContent
} from 'src/utils/pixels/spotmeEvents'
import { buildWhatsappUrl } from 'src/utils/whatsapp'

import OperatorPromoNote from 'src/components/OperatorPromo/OperatorPromoNote'
import { OperatorPromoKind } from 'src/types/graphql'
import Breadcrumbs from './Breadcrumbs'
import OnDemandWizard from './OnDemandWizard'

const HOW_IT_WORKS_HREF = '#como-funciona'
const ON_DEMAND_BASE_PATH = '/renta-mini-bodegas/almacenamiento-a-domicilio'

interface IProps {
  /** Human-readable city name. When set, the city-interpolated H1 variant is
   *  used and the form pre-locks to the city. */
  cityDisplayName?: string
  /** City-page variant: pre-selects (and visually locks) the form's pickup
   *  city. Mirrors the prop on the full-form section below. */
  coverage?: OnDemandCityConfig
  /** Parent-page variant: feeds the form's city select. */
  activeCities?: OnDemandCityConfig[]
  /** Parent-page geolocation hint. When the user resolves to a covered,
   *  partner-active market via `useUserLocation()` upstream, this is the
   *  matching city config — overrides the first-active-city fallback for
   *  both the hero price chip and the embedded `<OnDemandWizard>` default
   *  pickup city. Undefined on city pages (the URL pins the city) and on
   *  the parent page when geolocation is unresolved or out-of-area. */
  personalizedCity?: OnDemandCityConfig
  /** Indicative entry price (MXN/m²/mes) for the hero price chip. Defaults to
   *  the resolved city's `perSqmRates.twelveMonths` (cheapest duration tier —
   *  the most attention-grabbing lure), falling back to the first active
   *  city for the parent page. Per-m² matches the partner's actual pricing
   *  model; bundle pricing (`indicativePricing`) is reserved for the
   *  `IndicativeSizesGrid` section further down the page. */
  fromPrice?: number
}

/**
 * Editorial-vocabulary hero for Almacenamiento a Domicilio.
 *
 * Layout:
 *   - Mobile: breadcrumbs → eyebrow → H1 (italic accent) → subhead → framed
 *     van photo → price chip → 4-step strip → value props → secondary CTA row
 *     → form. The framed photo sits high in the flow because it's the most
 *     visceral "what we do" signal — putting it before the form lets users
 *     understand the service before being asked to commit.
 *   - Desktop (md): full-bleed van background image with a soft cream-300
 *     gradient overlay (left readable, right shows van blooming behind/around
 *     the form card). 12-column grid above the backdrop. Left rail
 *     (`md:col-span-7`) holds editorial copy + price chip + 4-step strip +
 *     secondary CTA row. Right rail (`md:col-span-5`) holds the
 *     <OnDemandWizard />, the conversion-critical surface for the page.
 *     The wizard absorbs the role of the legacy compact `<HeroLeadForm>`
 *     AND the standalone big `<OnDemandLeadForm>` section that used to
 *     sit further down — single canonical lead surface, multi-step
 *     progressive disclosure.
 *
 * `searchReturn` query param: when the user navigates here from a search
 * result via `<OnDemandSearchCard>` or `<OnDemandRecoveryCard>`, the cards
 * append `?searchReturn=<encoded asPath>` to the href. We read + validate it
 * here and pass through to `<Breadcrumbs>` so the breadcrumb trail can render
 * a "← Volver a tus resultados" affordance.
 *
 * Personalization chip: on the parent page, when `personalizedCity` resolves
 * via geolocation upstream, a small "Atendemos en {{city}}" pill renders
 * directly below the breadcrumbs. Click → navigates to the city-specific
 * landing (which has tighter editorial copy, locked form city, and full
 * coverage-zone list). Mirrors the Airbnb / Uber pattern: show what we
 * detected, make it correctable in one click, never silently lock the
 * user in. Skipped on city pages (already on the destination) and when
 * geolocation is unresolved (no chip = no false signal).
 *
 * GA4: fires `storage_on_demand_view` once per mount (with canonical-city
 * payload when on a city page) and `storage_on_demand_whatsapp` on the
 * hero's WhatsApp ghost CTA. The form components fire `_lead_start` and
 * `_lead` separately.
 *
 * The H1 is split on the first comma so we can italicize the second clause
 * editorially. Both H1 and H1_CITY copy variants ship with a comma so the
 * same logic handles parent and city headlines uniformly.
 */
const HeroOnDemand = ({
  cityDisplayName,
  coverage,
  activeCities,
  personalizedCity,
  fromPrice
}: IProps) => {
  const { t } = useTranslation('page_almacenamiento_a_domicilio')
  const router = useRouter()

  const rawReturn = router.query.searchReturn
  const searchReturnHref = isSafeReturnPath(rawReturn) ? rawReturn : undefined

  const eventCity = coverage?.canonicalSlug

  useEffect(() => {
    onDemandView({ city: eventCity })
    // Additive pixel-as-product fanout — keeps Meta DPA + GA4 product cohorts
    // able to retarget on-demand viewers as if they were listing viewers. See
    // viewOnDemandContent in spotmeEvents.ts.
    viewOnDemandContent({ city: eventCity })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const headline = cityDisplayName
    ? t('HERO.H1_CITY', { city: cityDisplayName })
    : t('HERO.H1')

  // Italicize the second clause of the H1 for editorial cadence.
  const [headlineLead, ...rest] = headline.split(',')
  const headlineTrail = rest.length > 0 ? rest.join(',').trim() : ''

  const whatsappMessage = cityDisplayName
    ? t('HERO.WHATSAPP_MSG_CITY', { city: cityDisplayName })
    : t('HERO.WHATSAPP_MSG')
  const whatsappUrl = buildWhatsappUrl(whatsappMessage)

  const handleWhatsappClick = () => {
    onDemandWhatsapp({ city: eventCity })
  }

  // Resolve the indicative entry price (MXN/m²/mes): explicit prop > city
  // coverage's cheapest tier > geolocation-personalized city's cheapest
  // tier > first active city's cheapest tier > `null` (skip the chip).
  // We anchor against `perSqmRates.twelveMonths` (the cheapest duration
  // tier) — partner pricing is per-m² with duration discounts, and the
  // cheapest tier is the strongest conversion lure. The duration-tier
  // section below the hero (`DurationTiers`) surfaces all three rates
  // explicitly so users aren't surprised at quote time. On the parent
  // page, `personalizedCity` is populated when the user's coords fall
  // inside a covered market's service area — anchors against THEIR city
  // instead of the first active city in the list.
  // The resolved market's own partner promotion (city page, or the
  // geolocated parent page) — quoted from the partner's site.
  const partnerPromo = (coverage ?? personalizedCity)?.partnerPromo

  const resolvedPrice =
    fromPrice ??
    coverage?.perSqmRates.twelveMonths ??
    personalizedCity?.perSqmRates.twelveMonths ??
    activeCities?.find((c) => c.partnerActive)?.perSqmRates.twelveMonths ??
    null

  // Per-city hero photo (MTY skyline / CDMX loading bay / parent crew
  // shot) — see the allocation table on `ON_DEMAND_PHOTOS` in
  // `src/utils/onDemand.ts`.
  const heroImage = getOnDemandHeroImage(
    coverage?.canonicalSlug ?? personalizedCity?.canonicalSlug
  )

  return (
    <section
      aria-labelledby="hero-on-demand-heading"
      className="relative w-full overflow-hidden bg-cream-300 pt-[calc(var(--nav-h)+24px)] pb-16 md:bg-ink-800 md:pt-[calc(var(--nav-h)+32px)] md:pb-24"
    >
      {/* Desktop backdrop — full-bleed editorial photo under an ink scrim
          (same directional left→right + bottom-up recipe as the home hero,
          `src/screens/Landing/Hero.tsx`). The old cream wash bleached the
          branded photography to ~35% — the scrim keeps the copy legible
          while the photo keeps its colour. Mobile is untouched: the wizard
          must stay in the first phone viewport, so the photo sits below it
          (see the mobile `<figure>` further down). */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-0 hidden md:block"
      >
        <Image
          src={heroImage}
          alt=""
          fill
          priority
          sizes="100vw"
          className="object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-ink-800/95 from-25% via-ink-800/70 via-55% to-ink-800/25" />
        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-ink-800/80 to-transparent" />
      </div>

      <div className="relative z-10 mx-auto w-full max-w-7xl px-5 md:px-10">
        <Breadcrumbs
          cityDisplayName={cityDisplayName}
          searchReturnHref={searchReturnHref}
        />

        {/* Personalization chip — renders only on the parent page when
            geolocation resolves to a covered, partner-active market.
            Click → navigates to the city-specific landing. The
            "Atendemos en {{city}}" framing matches the existing
            COVERAGE.TITLE_CITY voice on the page. */}
        {personalizedCity && !coverage && (
          <div className="mt-4">
            <Link
              href={`${ON_DEMAND_BASE_PATH}/${personalizedCity.canonicalSlug}`}
              className="group inline-flex items-center gap-2 rounded-full border border-blue-500/20 bg-white-500 px-3.5 py-1.5 text-[12px] font-medium text-ink-700 transition-colors hover:border-blue-500 hover:text-blue-500"
              aria-label={t('HERO.PERSONALIZED_CHIP_ARIA', {
                city: personalizedCity.displayShortName
              })}
            >
              <span
                className="size-1.5 shrink-0 rounded-full bg-blue-500"
                aria-hidden="true"
              />
              <span>
                {t('HERO.PERSONALIZED_CHIP', {
                  city: personalizedCity.displayShortName
                })}
              </span>
              <i
                className="si si-right text-[8px] transition-transform duration-200 group-hover:translate-x-0.5"
                aria-hidden="true"
              />
            </Link>
          </div>
        )}

        {/* Three grid children so we can split the left rail into "header"
            (above the wizard on mobile) and "media + price + CTAs" (below
            the wizard on mobile). Desktop layout is preserved via
            explicit row placement: header in row 1 left, media in row 2
            left, wizard spans both rows on the right. Mobile (grid-cols-1)
            falls back to natural DOM order: header → wizard → media —
            putting the conversion surface in the first viewport instead
            of after the photo + price tile + CTAs. */}
        <div className="mt-6 grid grid-cols-1 items-start gap-8 md:mt-10 md:grid-cols-12 md:gap-14">
          {/* Header rail — editorial copy only. Sits above the wizard on
              mobile, beside the wizard on desktop (row 1 / cols 1-7). Mobile
              gaps are deliberately tighter than desktop so the wizard's first
              input clears the fold on a 390px viewport (the header copy is the
              only thing above the conversion surface on mobile). */}
          <div className="flex flex-col gap-4 md:col-span-7 md:gap-7">
            <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
              {t('HERO.EYEBROW')}
            </p>

            <h1
              id="hero-on-demand-heading"
              className="max-w-3xl font-display text-[28px] leading-[1.1] font-medium tracking-[-0.015em] text-ink-700 md:text-[48px] md:leading-[1.05] md:text-white-500 lg:text-[56px]"
            >
              {headlineLead}
              {headlineTrail && (
                <>
                  {`, `}
                  <span className="font-display text-blue-500 italic">
                    {headlineTrail}
                  </span>
                </>
              )}
            </h1>

            <p className="max-w-2xl text-[15px] leading-relaxed text-ink-300 md:text-lg md:text-white-500/85">
              {t('HERO.SUBHEAD')}
            </p>
          </div>

          {/* Wizard rail — multi-step lead wizard (the conversion surface).
              Carries `id="cotizar"` so the existing hero anchor + mobile
              sticky CTA both scroll to step 1. Desktop: spans both grid
              rows on the right (cols 8-12) so the media rail can place
              underneath the header on the left without leaving an empty
              right-bottom cell. Mobile: stacks immediately after the
              header — moving it above the photo + price tile + secondary
              CTAs is the conversion lift (49 of 52 30-day sessions are
              mobile; the wizard previously rendered ~1.5 viewports below
              the fold). */}
          <div className="md:col-span-5 md:row-span-2">
            <OnDemandWizard
              coverage={coverage}
              activeCities={activeCities}
              personalizedCity={personalizedCity}
            />
          </div>

          {/* Media + price + CTA rail — reinforces the wizard. Mobile:
              stacks after the wizard (where users either kept scrolling
              after engaging or as a fallback for users who didn't). Desktop:
              sits in row 2 / cols 1-7, below the header on the left. */}
          <div className="flex flex-col gap-6 md:col-span-7 md:gap-7">
            {/* Mobile-only framed photo, full-bleed, below the wizard.
                Same per-city shot as the desktop backdrop
                (`getOnDemandHeroImage`); the bottom gradient anchors the
                caption. */}
            <figure className="relative -mx-5 aspect-5/3 w-[calc(100%+2.5rem)] overflow-hidden md:hidden">
              <Image
                src={heroImage}
                alt={t('HERO.IMAGE_CAPTION')}
                fill
                priority
                sizes="100vw"
                className="object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink-800/55 via-ink-800/10 to-transparent" />
              <figcaption className="absolute right-4 bottom-4 left-4 font-display text-[12px] tracking-[0.18em] text-white-500/85 uppercase">
                {t('HERO.IMAGE_CAPTION')}
              </figcaption>
            </figure>

            {/* Price tile — promoted from a single chip to a 2-line tile
                so the entry price is anchored against a concrete
                "what does this get me" cue. Highest-anxiety question on
                the page is "what will this cost me," so we surface
                concrete numbers up front. Italic Fraunces numerals
                continue the editorial accent vocabulary. */}
            {resolvedPrice !== null && (
              <div className="inline-flex w-fit flex-col gap-0.5 rounded-lg border border-blue-500/20 bg-white-500 px-4 py-3">
                <span className="font-display text-[20px] leading-none font-medium tracking-[-0.01em] text-blue-500 italic md:text-[24px]">
                  {t('HERO.PRICE_FROM', {
                    price: numberWithCommas(resolvedPrice)
                  })}
                </span>
                <span className="text-[12px] leading-tight text-ink-200 md:text-[13px]">
                  {t('HERO.PRICE_TILE_SUB')}
                </span>
              </div>
            )}

            {/* The partner's own promotion, quoted from its site (city page
                or geolocated parent page). Never names the partner. */}
            {partnerPromo && (
              <OperatorPromoNote
                className="w-fit"
                promo={{
                  kind: OperatorPromoKind.Perk,
                  headline: partnerPromo.headline,
                  operatorWording: partnerPromo.headline,
                  verifiedOn: partnerPromo.verifiedOn
                }}
              />
            )}

            {/* Secondary CTA row — WhatsApp ghost + "cómo funciona" anchor. */}
            <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleWhatsappClick}
                className="inline-flex items-center gap-2 rounded-full border border-ink-100/30 bg-white-500 px-5 py-2.5 text-[14px] font-medium text-ink-700 transition-colors hover:border-blue-500 hover:text-blue-500"
              >
                <WhatsAppIcon className="text-lg text-blue-500" />
                {t('HERO.WHATSAPP_CTA')}
              </a>
              <a
                href={HOW_IT_WORKS_HREF}
                className="text-[14px] font-medium text-blue-500 underline decoration-blue-500/40 underline-offset-4 transition-colors hover:text-blue-700 hover:decoration-blue-500 md:text-white-500 md:decoration-white-500/40 md:hover:text-blue-500 md:hover:decoration-blue-500"
              >
                {t('HERO.HOW_IT_WORKS_CTA')}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default React.memo(HeroOnDemand)
